import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { CHECKLIST_DOCUMENT, POH_DOCUMENT, PERMISSIONS, airworthinessLight, formatMinutes, holdRestrictions, inMaintenance, type AirworthinessLight, type NavDataState, type NavDataUnit } from '@jupi/shared'
import { MaintenanceBar, RemoteContent, Title, aircraftPictureUrl, formatDate, useAuth, useClubToday, useFleetNavData } from '@jupi/ui'
import { FuelCardPin, FuelGauge, scanUrl } from '../components/AircraftCards'
import { useAircraft, useAircraftDefects, useMaintenanceEvents, useMaintenanceSchedules } from '../api/queries'

// Traffic light: the card's border colour.
const LIGHT_COLOUR: Record<AirworthinessLight, 'error' | 'warning' | 'success'> = {
  aog: 'error',
  restricted: 'warning',
  airworthy: 'success',
}

const NAV_COLOUR: Record<NavDataState, string> = {
  current: 'text.secondary',
  expiring: 'warning.main',
  expired: 'error.main',
}

export default function FleetPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: aircraft = [], isLoading, error } = useAircraft()
  const { data: navUnits = [] } = useFleetNavData()
  // The unit that runs out first, per aircraft, for the line on its card.
  const navDue = new Map<string, NavDataUnit>()
  for (const u of navUnits) {
    const first = navDue.get(u.registration)
    if (!first || u.validUntil < first.validUntil) navDue.set(u.registration, u)
  }
  // How many hours each aircraft has left before its next maintenance.
  const { can } = useAuth()
  const { data: maintenance = [] } = useMaintenanceSchedules(can(PERMISSIONS.maintenanceRead))
  const { data: defects } = useAircraftDefects()
  const { data: maintenanceEvents = [] } = useMaintenanceEvents()
  const today = useClubToday()

  return (
    <Box>
      <Title label={t('aircraft.titlePlural')} />
      <RemoteContent isLoading={isLoading} error={error}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          }}
        >
          {aircraft.map((plane) => {
            const pictureUrl = aircraftPictureUrl(plane.registration, plane.pictureUpdatedAt)
            const upkeep = maintenance.find((m) => m.registration === plane.registration)
            // Published defects and hold items: a hold item releases the
            // aircraft but may still restrict it.
            const planeDefects = (defects?.published ?? []).filter((d) => d.registration === plane.registration)
            const light = airworthinessLight(planeDefects)
            const colour = LIGHT_COLOUR[light]
            const planeEvents = maintenanceEvents.filter((e) => e.registration === plane.registration)
            const inHangar = inMaintenance(planeEvents, today)
            const restrictions = [
              ...new Set(planeDefects.flatMap((d) => (d.status === 'held' ? holdRestrictions(d) : d.restriction))),
            ]
            const onHoldList = planeDefects.some((d) => d.status === 'held' && holdRestrictions(d).length > 0)
            return (
              <Card
                key={plane.registration}
                sx={{
                  position: 'relative',
                  border: 2,
                  // Grey for in maintenance; the chip says it in words too.
                  borderColor: inHangar ? 'grey.500' : `${colour}.main`,
                }}
              >
                {inHangar ? (
                  <Chip
                    clickable
                    icon={<Icon icon="mdi:wrench" />}
                    label={t('aircraft.inMaintenance')}
                    onClick={() => navigate(`/fleet/${plane.registration}`)}
                    sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, fontWeight: 700, bgcolor: 'grey.700', color: 'common.white', '& .MuiChip-icon': { color: 'inherit' } }}
                  />
                ) : light !== 'airworthy' ? (
                  // Beside the action area, not inside it: a button in a
                  // button is invalid. Goes to this aircraft's defects.
                  <Tooltip title={restrictions.map((r) => t(`aircraftDefect.restrictions.${r}`)).join(', ')}>
                    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                      {onHoldList ? (
                        <Chip
                          clickable
                          variant="filled"
                          label={t('aircraftDefect.holdItem')}
                          onClick={() => navigate(`/fleet/defects?registration=${plane.registration}`)}
                          sx={{ bgcolor: 'background.paper', fontWeight: 700 }}
                        />
                      ) : null}
                      <Chip
                        clickable
                        color={colour}
                        icon={<Icon icon={light === 'aog' ? 'mdi:hand-front-left' : 'mdi:alert-outline'} />}
                        label={t(`aircraftDefect.airworthiness.${light}`)}
                        onClick={() => navigate(`/fleet/defects?registration=${plane.registration}`)}
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>
                  </Tooltip>
                ) : null}
                <CardActionArea onClick={() => navigate(`/fleet/${plane.registration}`)}>
                  {pictureUrl ? (
                    <CardMedia
                      component="img"
                      image={pictureUrl}
                      alt={t('aircraft.picture.alt', { registration: plane.registration })}
                      loading="lazy"
                      sx={{ aspectRatio: '16 / 9', objectFit: 'cover' }}
                    />
                  ) : (
                    // Same 16:9 box without a photo, so the cards in a grid
                    // row line up whichever of them have one.
                    <Box
                      sx={{
                        aspectRatio: '16 / 9',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'action.hover',
                        color: 'text.disabled',
                      }}
                    >
                      <Icon icon="mdi:airplane" width={48} />
                    </Box>
                  )}
                  <CardContent>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                          {plane.registration}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plane.aircraftType}
                        </Typography>
                        {plane.seats ? (
                          <Tooltip title={t('aircraft.seats')}>
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', width: 'fit-content', color: 'text.secondary' }}>
                              <Icon icon="mdi:account" width={18} />
                              <Typography variant="body2">{plane.seats}</Typography>
                            </Stack>
                          </Tooltip>
                        ) : null}
                      </Box>
                      {!plane.available ? (
                        <Chip size="small" color="error" label={t('aircraft.unavailable')} />
                      ) : null}
                      {plane.totals || plane.mtow || navDue.has(plane.registration) ? (
                        // Total hours, landings, MTOW and navigation data, top right.
                        <Stack sx={{ alignItems: 'flex-start', fontVariantNumeric: 'tabular-nums' }}>
                          {plane.totals ? (
                            <>
                              <Tooltip title={t('aircraft.totalHours')}>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                  <Icon icon="mdi:clock-outline" width={18} />
                                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                    {formatMinutes(plane.totals.airframeMinutes)} h
                                  </Typography>
                                </Stack>
                              </Tooltip>
                              <Tooltip title={t('aircraft.totalLandings')}>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                  <Icon icon="mdi:airplane-landing" width={18} />
                                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                    {plane.totals.landings}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            </>
                          ) : null}
                          {plane.mtow ? (
                            <Tooltip title={t('aircraft.mtow')}>
                              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                <Icon icon="mdi:weight-kilogram" width={18} />
                                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                  {plane.mtow} kg
                                </Typography>
                              </Stack>
                            </Tooltip>
                          ) : null}
                          {navDue.get(plane.registration) ? (
                            <Tooltip title={`${t('navData.title')}: ${t('navData.validUntil', { date: formatDate(`${navDue.get(plane.registration)!.validUntil}T12:00:00Z`) })}`}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ alignItems: 'center', color: NAV_COLOUR[navDue.get(plane.registration)!.state] }}
                              >
                                <Icon icon="mdi:satellite-variant" width={18} />
                                <Typography variant="body2" sx={{ fontWeight: navDue.get(plane.registration)!.state === 'current' ? 400 : 700 }}>
                                  {formatDate(`${navDue.get(plane.registration)!.validUntil}T12:00:00Z`)}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      ) : null}
                    </Stack>
                    {plane.fuel ? (
                      <Box sx={{ mt: 1.5 }}>
                        <FuelGauge fuel={plane.fuel} />
                      </Box>
                    ) : null}
                    {upkeep?.schedule && upkeep.status ? (
                      <Box sx={{ mt: 1.5 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <MaintenanceBar schedule={upkeep.schedule} status={upkeep.status} compact />
                      </Box>
                    ) : null}
                  </CardContent>
                </CardActionArea>
                {plane.hasPoh || plane.hasChecklist || plane.hasFuelCardPin ? (
                  // The handbook and checklist, one tap from the fleet list.
                  // Beside the action area, not inside it: a link in a button is invalid.
                  <CardActions sx={{ px: 2, pb: 2, pt: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
                    {plane.hasPoh || plane.hasChecklist ? (
                      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: plane.hasPoh && plane.hasChecklist ? 'repeat(2, minmax(0, 1fr))' : '1fr' }}>
                        {plane.hasPoh ? (
                          <Button
                            variant="outlined"
                            component="a"
                            href={scanUrl(plane.registration, POH_DOCUMENT)}
                            target="_blank"
                            rel="noopener"
                            startIcon={<Icon icon="mdi:book-open-page-variant" />}
                            sx={{ minHeight: 40 }}
                          >
                            {t('aircraft.poh.short')}
                          </Button>
                        ) : null}
                        {plane.hasChecklist ? (
                          <Button
                            variant="outlined"
                            component="a"
                            href={scanUrl(plane.registration, CHECKLIST_DOCUMENT)}
                            target="_blank"
                            rel="noopener"
                            startIcon={<Icon icon="mdi:format-list-checks" />}
                            sx={{ minHeight: 40 }}
                          >
                            {t('aircraft.checklist.short')}
                          </Button>
                        ) : null}
                      </Box>
                    ) : null}
                    {plane.hasFuelCardPin ? <FuelCardPin registration={plane.registration} /> : null}
                  </CardActions>
                ) : null}
              </Card>
            )
          })}
        </Box>
      </RemoteContent>
    </Box>
  )
}
